import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { ConversationType, MentionUser, ProjectMessage } from "../types";

/**
 * Kolekcja, pod którą wisi konwersacja: „projects" (przedsięwzięcie) albo
 * „agreements" (umowa ramowa). Backend serwuje obie pod tym samym kształtem adresów
 * (`/<kolekcja>/<id>/messages/`, `…/mention-users/`, `…/messages/<id>/pin/`)
 * i tym samym kształtem odpowiedzi, więc jeden zestaw haków obsługuje oba miejsca.
 * Domyślnie „projects" — wywołania sprzed dołożenia umów zostają nietknięte.
 */
export type ConversationBase = "projects" | "agreements";

const messagesKey = (
  projectId: string,
  type: ConversationType,
  base: ConversationBase = "projects",
) => ["project-messages", base, projectId, type];

export function useProjectMessages(
  projectId: string,
  type: ConversationType,
  base: ConversationBase = "projects",
) {
  return useQuery({
    queryKey: messagesKey(projectId, type, base),
    queryFn: () =>
      apiClient
        .get<{ data: ProjectMessage[] }>(`/${base}/${projectId}/messages/`, {
          params: { conversation_type: type },
        })
        .then((r) => r.data.data),
    enabled: !!projectId,
  });
}

interface CreateMessagePayload {
  projectId: string;
  conversationType: ConversationType;
  content: string;
  parent?: string | null;
  mentionIds?: string[];
  files?: File[];
  base?: ConversationBase;
}

export function useCreateProjectMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      conversationType,
      content,
      parent,
      mentionIds = [],
      files = [],
      base = "projects",
    }: CreateMessagePayload) => {
      if (files.length > 0) {
        const fd = new FormData();
        fd.append("conversation_type", conversationType);
        fd.append("content", content);
        if (parent) fd.append("parent", parent);
        mentionIds.forEach((id) => fd.append("mention_ids", id));
        files.forEach((f) => fd.append("files", f));
        const r = await apiClient.post<{ data: ProjectMessage }>(
          `/${base}/${projectId}/messages/`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        return r.data.data;
      }
      const r = await apiClient.post<{ data: ProjectMessage }>(
        `/${base}/${projectId}/messages/`,
        {
          conversation_type: conversationType,
          content,
          parent: parent ?? null,
          mention_ids: mentionIds,
        },
      );
      return r.data.data;
    },
    onSuccess: (_, { projectId, conversationType, base = "projects" }) => {
      qc.invalidateQueries({ queryKey: messagesKey(projectId, conversationType, base) });
    },
  });
}

export function useDeleteProjectMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      messageId,
      base = "projects",
    }: {
      projectId: string;
      messageId: string;
      conversationType: ConversationType;
      base?: ConversationBase;
    }) => apiClient.delete(`/${base}/${projectId}/messages/${messageId}/`),
    onSuccess: (_, { projectId, conversationType, base = "projects" }) => {
      qc.invalidateQueries({ queryKey: messagesKey(projectId, conversationType, base) });
    },
  });
}

export function usePinProjectMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      messageId,
      base = "projects",
    }: {
      projectId: string;
      messageId: string;
      conversationType: ConversationType;
      base?: ConversationBase;
    }) =>
      apiClient
        .patch<{ data: ProjectMessage }>(`/${base}/${projectId}/messages/${messageId}/pin/`)
        .then((r) => r.data.data),
    onSuccess: (_, { projectId, conversationType, base = "projects" }) => {
      qc.invalidateQueries({ queryKey: messagesKey(projectId, conversationType, base) });
    },
  });
}

export function useMentionUsers(
  projectId: string,
  conversationType: ConversationType,
  enabled: boolean,
  base: ConversationBase = "projects",
) {
  return useQuery({
    queryKey: ["project-mention-users", base, projectId, conversationType],
    queryFn: () =>
      apiClient
        .get<{ data: MentionUser[] }>(`/${base}/${projectId}/mention-users/`, {
          params: { conversation_type: conversationType },
        })
        .then((r) => r.data.data),
    enabled: enabled && !!projectId,
  });
}
